import { useCallback } from "react";
import useOrganizations from "@/features/organizations/useOrganizations";
import pluralize from "pluralize";

export interface Terminology {
  product: string; // "plato" / "producto"
  catalog: string; // "menú" / "catálogo"
  establishment: string; // "restaurante" / "tienda"
  atEstablishment: string; // "en el restaurante" / "en la tienda"
}

const termsMap: Record<string, Terminology> = {
  "food-and-beverages": {
    product: "plato",
    catalog: "menú",
    establishment: "restaurante",
    atEstablishment: "en el restaurante",
  },
  "other": {
    product: "producto",
    catalog: "catálogo",
    establishment: "tienda",
    atEstablishment: "en la tienda",
  },
};

pluralize.addIrregularRule("plato", "platos");
pluralize.addIrregularRule("producto", "productos");
pluralize.addIrregularRule("catálogo", "catálogos");

export const useTerminology = () => {
  const { currentOrganization } = useOrganizations();
  const industry = currentOrganization?.industry || "food-and-beverages";

  // Fallback to food-and-beverages terms for standard restaurant/cafeteria/bar configs
  const terms = termsMap[industry] || termsMap["food-and-beverages"];

  const t = useCallback(
    (
      key: keyof Terminology,
      countOrOptions?: number | { count?: number; capitalize?: boolean },
      capitalizeOption = false
    ) => {
      let count: number | undefined = undefined;
      let capitalize = capitalizeOption;

      if (typeof countOrOptions === "object" && countOrOptions !== null) {
        count = countOrOptions.count;
        if (countOrOptions.capitalize !== undefined) {
          capitalize = countOrOptions.capitalize;
        }
      } else if (typeof countOrOptions === "number") {
        count = countOrOptions;
      }

      let word = terms[key] || "";
      if (count !== undefined && count !== 1) {
        word = pluralize(word, count);
      }
      if (capitalize && word.length > 0) {
        word = word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    },
    [terms]
  );

  return {
    t,
    product: terms.product,
    catalog: terms.catalog,
  };
};

export default useTerminology;
